import { ActivityEntityName, EntityFlag, FlagCloseRequest, FlagCreateRequest, FlagCqrsActions, MetadataEntityFieldEditorType, MetadataEntityFieldType, SurfaceType } from '@jms/model';
import { EntityDataServiceRaw } from '@workbuddy/services/entities/entity.data.service.raw';
import type { WorkBuddyDialogApi } from '@workbuddy/ui/v2/blocks/dialog';
import { defineSurface, defineSurfaceSchema, openFormSurfaceDialog } from '@workbuddy/ui/v2/surface.engine';
import { firstValueFrom } from 'rxjs';
import { buildFlagCreateFormSchema, FlagCreateFormModel } from '../surface/form/flag.create.form.schema';

/** Read-only context + editable outcome. Dialog title owns “Resolve flag”. */
type FlagResolveFormModel = {
  category?: string;
  message?: string;
  outcome?: string;
};

/** Bulk close keeps a few Close commands in flight: quick for a page of rows without flooding the API. */
const BULK_CLOSE_CONCURRENCY = 4;

export interface FlagBulkCloseResult {
  closed: EntityFlag[];
  failures: { flag: EntityFlag; message: string }[];
}

/** Opens flag create via generic Form surface dialog. */
export async function openFlagCreateForm(dialogs: WorkBuddyDialogApi, entities: EntityDataServiceRaw, options: {
  readonly parentEntity: ActivityEntityName;
  readonly parentId: string;
}): Promise<EntityFlag | undefined> {

  return openFormSurfaceDialog<EntityFlag>(dialogs, {
    title: 'Add flag',
    createDefinition: api => defineSurface({
      schema: buildFlagCreateFormSchema(),
      initialModel: { category: null, message: '' } satisfies FlagCreateFormModel,
      onCancel: () => api.dismiss(),
      onSubmit: async event => {
        const model = event.model as FlagCreateFormModel;
        const category = Array.isArray(model.category) ? model.category[0] : model.category;
        if (!category || !model.message?.trim()) throw new Error('Category and message are required.');
        const payload: FlagCreateRequest = {
          category,
          entity: options.parentEntity,
          entityId: options.parentId,
          message: model.message.trim(),
          source: 'manual',
        };
        const saved = await firstValueFrom(entities.save<EntityFlag>(ActivityEntityName.Flag, payload));
        api.close(saved);
        return saved;
      },
    }),
  });
}

/**
 * Resolve flag dialog hierarchy:
 *   Resolve flag          ← dialog title only
 *   Category / Message    ← readonly fields (context)
 *   Outcome               ← editable action
 *
 * Avoids panel title = category name (e.g. “Job”), which competed with the dialog title.
 */
/** `outcomeRequired`: the category's close is refused without one server-side, so the form asks for it up front. */
export async function openFlagResolveForm(dialogs: WorkBuddyDialogApi, entities: EntityDataServiceRaw, flag: EntityFlag, options: { readonly outcomeRequired?: boolean } = {}): Promise<EntityFlag | undefined> {

  return openFormSurfaceDialog<EntityFlag>(dialogs, {
    title: 'Resolve flag',
    createDefinition: api => defineSurface({
      schema: defineSurfaceSchema(ActivityEntityName.Flag, SurfaceType.Form)
        .model<FlagResolveFormModel>()
        .body(body => body
          .section('context', section => section
            .readonly(true)
            .columns(1)
            .field({
              bind: 'category',
              metadata: { displayName: 'Category', type: MetadataEntityFieldType.String },
            })
            .field({
              bind: 'message',
              metadata: { displayName: 'Message', type: MetadataEntityFieldType.String, editor: MetadataEntityFieldEditorType.TextArea },
            }))
          .section('resolution', section => section
            .columns(1)
            .field({
              bind: 'outcome',
              required: options.outcomeRequired === true,
              metadata: { displayName: 'Outcome', type: MetadataEntityFieldType.String, editor: MetadataEntityFieldEditorType.TextArea },
            })))
        .build(),
      initialModel: {
        category: flag.category?.name ?? '',
        message: flag.message ?? '',
        outcome: '',
      } satisfies FlagResolveFormModel,
      onCancel: () => api.dismiss(),
      onSubmit: async event => {
        const model = event.model as FlagResolveFormModel;
        const outcome = model.outcome?.trim() || undefined;
        if (options.outcomeRequired && !outcome) throw new Error('An outcome is required to close this flag.');
        const payload: FlagCloseRequest = { flagId: flag._id, outcome };
        const result = await firstValueFrom(entities.executeCommand<EntityFlag>(ActivityEntityName.Flag, FlagCqrsActions.Close, payload));
        api.close(result ?? flag);
        return result;
      },
    }),
  });
}

/** One outcome for a set of flags about to be closed together. Undefined when dismissed. */
export async function promptFlagsOutcome(dialogs: WorkBuddyDialogApi, count: number, options: { readonly outcomeRequired?: boolean } = {}): Promise<string | undefined> {

  const answer = await openFormSurfaceDialog<{ outcome?: string }>(dialogs, {
    title: count === 1 ? 'Resolve flag' : `Resolve ${count} flags`,
    createDefinition: api => defineSurface({
      schema: defineSurfaceSchema(ActivityEntityName.Flag, SurfaceType.Form)
        .model<FlagResolveFormModel>()
        .body(body => body
          .section('resolution', section => section
            .columns(1)
            .field({
              bind: 'outcome',
              required: options.outcomeRequired === true,
              metadata: { displayName: 'Outcome', type: MetadataEntityFieldType.String, editor: MetadataEntityFieldEditorType.TextArea },
            })))
        .build(),
      initialModel: { outcome: '' } satisfies FlagResolveFormModel,
      onCancel: () => api.dismiss(),
      onSubmit: async event => {
        const outcome = (event.model as FlagResolveFormModel).outcome?.trim() || undefined;
        if (options.outcomeRequired && !outcome) throw new Error('An outcome is required to close these flags.');
        const result = { outcome };
        api.close(result);
        return result;
      },
    }),
  });

  return answer ? answer.outcome ?? '' : undefined;
}

/**
 * One Close per flag with the same outcome, a few in flight. The server still judges each close
 * (rights, written outcome, already closed), so failures are reported per flag rather than aborting.
 */
export async function closeFlags(entities: EntityDataServiceRaw, flags: readonly EntityFlag[], outcome: string | undefined): Promise<FlagBulkCloseResult> {

  const result: FlagBulkCloseResult = { closed: [], failures: [] };
  const queue = [...flags];
  const worker = async () => {
    for (let flag = queue.shift(); flag; flag = queue.shift()) {
      try {
        const payload: FlagCloseRequest = { flagId: flag._id, outcome };
        result.closed.push(await firstValueFrom(entities.executeCommand<EntityFlag>(ActivityEntityName.Flag, FlagCqrsActions.Close, payload)) ?? flag);
      } catch (error) {
        result.failures.push({ flag, message: (error as { error?: { message?: string } })?.error?.message ?? (error instanceof Error ? error.message : 'The flag could not be closed.') });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(BULK_CLOSE_CONCURRENCY, flags.length) }, worker));

  return result;
}
